import React, { useState, useCallback, useEffect, useContext } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { NavContext } from 'components/privateRoute/PrivateRoute';
import moment from 'moment';
import AdminHeader from 'components/header';
import LayoutContainer from 'components/layout/LayoutContainer';
import Card from 'components/inputs/Card';
import Button from 'components/inputs/Button';
import DateTimePicker from 'components/inputs/DateTimePicker';
import Footer from 'components/general/Footer';
import { IoSearchOutline } from 'react-icons/io5';
import { useSelector, useDispatch } from 'react-redux';
import { RATING_AGGREGATION } from 'actions/rating';


const TagAnalysis = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { navigateTo } = useContext(NavContext);
  const profileData = useSelector((state) => state.profile.userProfile);
  const isLoading = useSelector((state) => state.rating.isAggregationLoading);
  const aggregation = useSelector((state) => state.rating.aggregation);
  const currentUserName = profileData?.name || '';

  const passedFrom = location.state?.from;
  const passedTo = location.state?.to;

  const [startDate, setStartDate] = useState(passedFrom ? moment(passedFrom) : moment().startOf('day'));
  const [endDate, setEndDate] = useState(passedTo ? moment(passedTo) : moment().endOf('day'));
  const [appliedRange, setAppliedRange] = useState(passedFrom ? { from: moment(passedFrom), to: moment(passedTo) } : null);

  const tagData = aggregation && Array.isArray(aggregation.tags) ? aggregation : null;

  const fetchAggregation = useCallback((from, to) => {
    const payload = {
      type: 'tag',
      from: moment(from).utc(),
      to: moment(to).utc(),
    };
    setAppliedRange({ from, to });
    dispatch({ type: RATING_AGGREGATION.REQUEST, payload });
  }, [dispatch]);

  useEffect(() => {
    if (passedFrom) {
      navigate(location.pathname, { replace: true, state: {} });
    }
    fetchAggregation(startDate, endDate);
  }, []);

  const applyFilter = useCallback(() => {
    fetchAggregation(startDate, endDate);
  }, [startDate, endDate, fetchAggregation]);

  const totalCount = tagData ? (tagData.totalTaggedIssues ?? tagData.tags.reduce((sum, tg) => sum + tg.count, 0)) : 0;

  return (
    <LayoutContainer>
      <AdminHeader
        title={t('ratingReview.tagAnalysis')}
        backTo="/ratingReviewReport"
        onBack={() => navigateTo('/ratingReviewReport', { state: { from: moment(startDate).toISOString(), to: moment(endDate).toISOString() } })}
      />
      <div className="page-content-wrapper scrollable">
        <div className="tag-analysis--main">
          <Card>
            <div className="tag-analysis__filter">
              <Row className="align-items-center" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
                <Col xs="auto">
                  <DateTimePicker
                    onChangeOfStartDate={(item) => setStartDate(item)}
                    onChangeOfEndDate={(item) => setEndDate(item)}
                    initialValueOfStartDate={startDate}
                    initialValueOfEndDate={endDate}
                  />
                </Col>
                <Col xs="auto">
                  <Button className="report-search-button" onClick={applyFilter}>
                    <IoSearchOutline />
                    <span>{t('button.search')}</span>
                  </Button>
                </Col>
                {tagData && (
                  <Col xs="auto" className="ms-auto tag-analysis__summary-inline">
                    <span className="tag-analysis__summary-label">Total Issues / Tags</span>
                    <span className="tag-analysis__summary-value">{totalCount}</span>
                    {appliedRange && (
                      <span className="tag-analysis__summary-range">
                        {`Date Range: ${moment(appliedRange.from).format('YYYY-MM-DD')} — ${moment(appliedRange.to).format('YYYY-MM-DD')}`}
                      </span>
                    )}
                  </Col>
                )}
              </Row>
            </div>
          </Card>

          {tagData && (
            <div className="tag-analysis__list">
              {tagData.tags.filter((tag) => tag.count > 0).map((tag) => {
                const pct = tag.percentage != null ? Number(tag.percentage).toFixed(1) : '0.0';
                return (
                  <div key={tag.key} className="tag-analysis__list-item">
                    <span className="tag-analysis__list-name">{tag.key}</span>
                    <div className="tag-analysis__list-right">
                      <span className="tag-analysis__list-pct">{pct}%</span>
                      <span className="tag-analysis__list-count">{tag.count}</span>
                      <span className="tag-analysis__list-total">/ {tag.totalReviews} reviews</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {!tagData && isLoading && (
            <Card>
              <div className="tag-analysis__empty">Loading...</div>
            </Card>
          )}
        </div>
      </div>
      {currentUserName === 'CZ Tenant Admin' ? null : <Footer />}
    </LayoutContainer>
  );
};

export default TagAnalysis;
